import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiGet } from '../lib/api';
import { WrapForm, type FormSpec, type Product } from '../components/WrapForm';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ProductsResponse = { products: Product[] };
type SchemaResponse = FormSpec;

// ---------------------------------------------------------------------------
// WrapConfig route
// Mounted at /wrap-config, guarded by RequireAuth in App.tsx.
// ---------------------------------------------------------------------------
export function WrapConfig() {
  const { me } = useAuth();
  const tenant = me?.tenants[0];

  const [products, setProducts] = useState<Product[] | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formSpec, setFormSpec] = useState<FormSpec | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);

  // --- Load products for this tenant ---
  useEffect(() => {
    if (!tenant) return;
    apiGet<ProductsResponse>(
      `/api/products?tenantSlug=${encodeURIComponent(tenant.slug)}`,
    )
      .then((r) => setProducts(r.products))
      .catch((e: Error) => setProductsError(e.message));
  }, [tenant]);

  // --- Load wrap schema when a product is selected ---
  useEffect(() => {
    if (!selectedProduct) {
      setFormSpec(null);
      setSchemaError(null);
      return;
    }
    setSchemaLoading(true);
    setSchemaError(null);
    apiGet<SchemaResponse>(
      `/api/engines/${encodeURIComponent(selectedProduct.engine_slug)}/wrap-schema`,
    )
      .then((spec) => setFormSpec(spec))
      .catch((e: Error) => setSchemaError(e.message))
      .finally(() => setSchemaLoading(false));
  }, [selectedProduct]);

  function handleSaved(updated: Product) {
    setProducts((prev) =>
      prev ? prev.map((p) => (p.id === updated.id ? updated : p)) : prev,
    );
    setSelectedProduct(updated);
  }

  // --- No workspace ---
  if (!tenant) {
    return (
      <main className="min-h-screen px-6 py-12">
        <div className="mx-auto max-w-3xl">
          <BackLink />
          <section className="mt-8 rounded-lg border border-amber-700/40 bg-amber-900/10 p-6">
            <h2 className="text-lg font-semibold">No workspace yet</h2>
            <p className="mt-2 text-slate-400">
              You need an active workspace before configuring products.{' '}
              <a className="text-sky-400 hover:underline" href="https://clarity.app/signup">
                Start with Pilot — $149/mo
              </a>
              .
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <BackLink />

        <header className="mt-4">
          <p className="text-xs uppercase tracking-widest text-sky-400">
            {tenant.name}
          </p>
          <h1 className="mt-1 text-2xl font-semibold">Wrap config</h1>
          <p className="mt-1 text-slate-400 text-sm">
            Configure your engine: copy, pricing, ICP labels, output format.
          </p>
        </header>

        {/* Product list / edit view */}
        {selectedProduct ? (
          <section className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold">{selectedProduct.name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedProduct.engine_slug} &middot;{' '}
                  <StatusPill status={selectedProduct.status} />
                </p>
              </div>
              <button
                onClick={() => setSelectedProduct(null)}
                className="text-sm text-slate-400 hover:text-slate-200"
              >
                ← Back to products
              </button>
            </div>

            {schemaLoading && (
              <p className="text-slate-500 text-sm">Loading schema…</p>
            )}
            {schemaError && (
              <p className="text-red-400 text-sm" role="alert">
                Failed to load form schema: {schemaError}
              </p>
            )}
            {formSpec && (
              <WrapForm
                spec={formSpec}
                product={selectedProduct}
                onSaved={handleSaved}
                onCancel={() => setSelectedProduct(null)}
              />
            )}
          </section>
        ) : (
          <section className="mt-8">
            <ProductList
              products={products}
              error={productsError}
              onEdit={(p) => setSelectedProduct(p)}
            />
          </section>
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BackLink() {
  return (
    <Link to="/dashboard" className="text-sm text-slate-400 hover:text-slate-200">
      ← Dashboard
    </Link>
  );
}

function StatusPill({ status }: { status: string }) {
  const colours: Record<string, string> = {
    draft: 'text-amber-400',
    live: 'text-emerald-400',
    paused: 'text-slate-400',
    closed: 'text-red-400',
  };
  return (
    <span className={colours[status] ?? 'text-slate-400'}>{status}</span>
  );
}

type ProductListProps = {
  products: Product[] | null;
  error: string | null;
  onEdit: (p: Product) => void;
};

function ProductList({ products, error, onEdit }: ProductListProps) {
  if (error) {
    return (
      <p className="text-red-400 text-sm" role="alert">
        Failed to load products: {error}
      </p>
    );
  }

  if (!products) {
    return <p className="text-slate-500 text-sm">Loading products…</p>;
  }

  if (products.length === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-6 text-center">
        <h3 className="font-semibold">No products yet</h3>
        <p className="mt-2 text-sm text-slate-400">
          Products are created through the AI-CTO intake (Task 1.27 — coming soon).
        </p>
        {/*
          Phase 1 stub: the "Create your first product" CTA is intentionally a no-op.
          Product creation lives in the AI-CTO intake flow (Task 1.27). This placeholder
          preserves the UX surface area without wiring a flow that doesn't exist yet.
        */}
        <button
          disabled
          className="mt-4 inline-block rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-500 cursor-not-allowed"
          title="Product creation is available in the AI-CTO intake (coming soon)"
        >
          Create your first product
        </button>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {products.map((p) => (
        <li
          key={p.id}
          className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 p-5"
        >
          <div>
            <p className="font-semibold">{p.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {p.engine_slug} &middot; <StatusPill status={p.status} />
            </p>
          </div>
          <button
            onClick={() => onEdit(p)}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-sky-500 hover:text-sky-400"
          >
            Edit wrap
          </button>
        </li>
      ))}
    </ul>
  );
}
